export interface PasswordChecks {
  hasMinLength: boolean
  hasLowercase: boolean
  hasUppercase: boolean
  hasNumber: boolean
  hasSymbol: boolean
  isStrong: boolean
}

export interface PasswordPolicy {
  minLength?: number
  minLowerCase?: number
  minUpperCase?: number
  minNumber?: number
  minSymbol?: number
}

export const getPasswordChecks = (password: string, minLengthOrPolicy?: number | PasswordPolicy): PasswordChecks => {
  const value = String(password || '')

  // Determine if second argument is a number (minLength) or a PasswordPolicy object
  let minLength = 8
  let minLowerCase = 1
  let minUpperCase = 1
  let minNumber = 1
  let minSymbol = 0

  if (typeof minLengthOrPolicy === 'number') {
    minLength = minLengthOrPolicy
    // Use defaults for other policy fields
  } else if (typeof minLengthOrPolicy === 'object' && minLengthOrPolicy !== null) {
    minLength = minLengthOrPolicy.minLength ?? 8
    minLowerCase = minLengthOrPolicy.minLowerCase ?? 1
    minUpperCase = minLengthOrPolicy.minUpperCase ?? 1
    minNumber = minLengthOrPolicy.minNumber ?? 1
    minSymbol = minLengthOrPolicy.minSymbol ?? 0
  }
  // Check minimum length
  const hasMinLength = value.length >= minLength

  // Check lowercase letters (count occurrences)
  const lowercaseMatches = (value.match(/[a-z]/g) || []).length
  const hasLowercase = lowercaseMatches >= minLowerCase

  // Check uppercase letters (count occurrences)
  const uppercaseMatches = (value.match(/[A-Z]/g) || []).length
  const hasUppercase = uppercaseMatches >= minUpperCase

  // Check numbers (count occurrences)
  const numberMatches = (value.match(/\d/g) || []).length
  const hasNumber = numberMatches >= minNumber

  // Check symbols (count occurrences) - only if required
  const symbolMatches = (value.match(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g) || []).length
  const hasSymbol = minSymbol === 0 || symbolMatches >= minSymbol

  // Password is strong if all requirements are met
  const isStrong = hasMinLength && hasLowercase && hasUppercase && hasNumber && hasSymbol

  return { hasMinLength, hasLowercase, hasUppercase, hasNumber, hasSymbol, isStrong }
}

/**
 * Builds a human-readable password policy requirement message from a PasswordPolicy object
 * @param policy - The password policy object containing requirements
 * @returns A formatted string describing the password requirements
 */
export const buildPasswordPolicyMessage = (policy: PasswordPolicy | null | undefined): string => {
  if (!policy) {
    return 'Password must be at least 8 characters and include uppercase, lowercase, and a number'
  }

  const requirements: string[] = []

  if (policy.minLength) {
    requirements.push(`at least ${policy.minLength} characters`)
  }

  if (policy.minLowerCase) {
    requirements.push(`${policy.minLowerCase} lowercase letter${policy.minLowerCase > 1 ? 's' : ''}`)
  }

  if (policy.minUpperCase) {
    requirements.push(`${policy.minUpperCase} uppercase letter${policy.minUpperCase > 1 ? 's' : ''}`)
  }

  if (policy.minNumber) {
    requirements.push(`${policy.minNumber} number${policy.minNumber > 1 ? 's' : ''}`)
  }

  if (policy.minSymbol) {
    requirements.push(`${policy.minSymbol} symbol${policy.minSymbol > 1 ? 's' : ''}`)
  }

  if (requirements.length > 0) {
    return `Password must include ${requirements.join(', ')}`
  }

  return 'Password must be at least 8 characters and include uppercase, lowercase, and a number'
}
